/**
 * SupabaseAppConfigRepository
 *
 * Supabase implementation of IAppConfigRepository
 */

import { supabase } from '@/shared/config/supabase';
import { AppConfig } from '../../domain/entities/AppConfig';
import type {
  IAppConfigRepository,
  UpdateAppConfigParams,
  LogoVariant,
} from '../../domain/repositories/IAppConfigRepository';

const STORAGE_BUCKET = 'app-branding';

export class SupabaseAppConfigRepository implements IAppConfigRepository {
  async getConfig(): Promise<AppConfig> {
    const { data, error } = await supabase.from('app_config').select('*').eq('id', 1).single();

    if (error) {
      console.error('Error fetching app config:', error);
      // Return default config if table doesn't exist yet
      return new AppConfig({
        id: 1,
        appName: 'Coursue',
        appTagline: 'Plataforma de aprendizaje moderna',
        primaryColor: '#6D5DFE',
      });
    }

    return this.mapToEntity(data);
  }

  async updateConfig(params: UpdateAppConfigParams): Promise<AppConfig> {
    const dbData = this.mapToDbFormat(params);

    const { data, error } = await supabase
      .from('app_config')
      .update(dbData)
      .eq('id', 1)
      .select()
      .single();

    if (error) {
      throw new Error(`Error al actualizar configuracion: ${error.message}`);
    }

    return this.mapToEntity(data);
  }

  async uploadLogo(file: Blob, fileName: string): Promise<string> {
    const { error: uploadError } = await supabase.storage
      .from(STORAGE_BUCKET)
      .upload(fileName, file, {
        upsert: true,
        contentType: 'image/png',
      });

    if (uploadError) {
      throw new Error(`Error al subir logo: ${uploadError.message}`);
    }

    const { data } = supabase.storage.from(STORAGE_BUCKET).getPublicUrl(fileName);
    return data.publicUrl;
  }

  async deleteLogos(variant?: LogoVariant): Promise<void> {
    const { data: files, error: listError } = await supabase.storage.from(STORAGE_BUCKET).list();

    if (listError) {
      throw new Error(`Error al listar logos: ${listError.message}`);
    }

    if (files && files.length > 0) {
      // Filter files based on variant
      let filesToDelete = files;
      if (variant === 'light') {
        filesToDelete = files.filter(f => !f.name.includes('-dark-'));
      } else if (variant === 'dark') {
        filesToDelete = files.filter(f => f.name.includes('-dark-'));
      }

      if (filesToDelete.length > 0) {
        const filePaths = filesToDelete.map(f => f.name);
        const { error: deleteError } = await supabase.storage.from(STORAGE_BUCKET).remove(filePaths);

        if (deleteError) {
          throw new Error(`Error al eliminar logos: ${deleteError.message}`);
        }
      }
    }

    // Clear URLs in database based on variant
    const updateData: Record<string, null> = {};

    if (!variant || variant === 'light') {
      updateData.logo_original_url = null;
      updateData.logo_favicon_url = null;
      updateData.logo_small_url = null;
      updateData.logo_medium_url = null;
      updateData.logo_large_url = null;
    }

    if (!variant || variant === 'dark') {
      updateData.logo_dark_original_url = null;
      updateData.logo_dark_favicon_url = null;
      updateData.logo_dark_small_url = null;
      updateData.logo_dark_medium_url = null;
      updateData.logo_dark_large_url = null;
    }

    await supabase.from('app_config').update(updateData).eq('id', 1);
  }

  private mapToEntity(data: Record<string, unknown>): AppConfig {
    return new AppConfig({
      id: data.id as number,
      appName: data.app_name as string,
      appTagline: data.app_tagline as string | undefined,
      // Light mode logos
      logoOriginalUrl: data.logo_original_url as string | undefined,
      logoFaviconUrl: data.logo_favicon_url as string | undefined,
      logoSmallUrl: data.logo_small_url as string | undefined,
      logoMediumUrl: data.logo_medium_url as string | undefined,
      logoLargeUrl: data.logo_large_url as string | undefined,
      // Dark mode logos
      logoDarkOriginalUrl: data.logo_dark_original_url as string | undefined,
      logoDarkFaviconUrl: data.logo_dark_favicon_url as string | undefined,
      logoDarkSmallUrl: data.logo_dark_small_url as string | undefined,
      logoDarkMediumUrl: data.logo_dark_medium_url as string | undefined,
      logoDarkLargeUrl: data.logo_dark_large_url as string | undefined,
      primaryColor: data.primary_color as string | undefined,
      updatedAt: data.updated_at ? new Date(data.updated_at as string) : undefined,
      updatedBy: data.updated_by as string | undefined,
    });
  }

  private mapToDbFormat(params: UpdateAppConfigParams): Record<string, unknown> {
    const dbData: Record<string, unknown> = {};

    if (params.appName !== undefined) dbData.app_name = params.appName;
    if (params.appTagline !== undefined) dbData.app_tagline = params.appTagline;
    // Light mode logos
    if (params.logoOriginalUrl !== undefined) dbData.logo_original_url = params.logoOriginalUrl;
    if (params.logoFaviconUrl !== undefined) dbData.logo_favicon_url = params.logoFaviconUrl;
    if (params.logoSmallUrl !== undefined) dbData.logo_small_url = params.logoSmallUrl;
    if (params.logoMediumUrl !== undefined) dbData.logo_medium_url = params.logoMediumUrl;
    if (params.logoLargeUrl !== undefined) dbData.logo_large_url = params.logoLargeUrl;
    // Dark mode logos
    if (params.logoDarkOriginalUrl !== undefined) dbData.logo_dark_original_url = params.logoDarkOriginalUrl;
    if (params.logoDarkFaviconUrl !== undefined) dbData.logo_dark_favicon_url = params.logoDarkFaviconUrl;
    if (params.logoDarkSmallUrl !== undefined) dbData.logo_dark_small_url = params.logoDarkSmallUrl;
    if (params.logoDarkMediumUrl !== undefined) dbData.logo_dark_medium_url = params.logoDarkMediumUrl;
    if (params.logoDarkLargeUrl !== undefined) dbData.logo_dark_large_url = params.logoDarkLargeUrl;
    if (params.primaryColor !== undefined) dbData.primary_color = params.primaryColor;

    return dbData;
  }
}
